import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Buildings, ArrowLeft, ArrowsClockwise, Receipt, Coins, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtEUR2 = (n) => {
  const v = Number(n || 0);
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(v);
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES") : "—";

const ESTADO_LABEL = {
  pendiente: "Pendiente",
  facturado: "Facturado",
  cobrado: "Cobrado",
};
const ESTADO_CLS = {
  pendiente: "bg-slate-50 text-slate-700 border-slate-200",
  facturado: "bg-amber-50 text-amber-700 border-amber-200",
  cobrado: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function LiquidacionesAseguradoras() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [filtroAseg, setFiltroAseg] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      if (filtroAseg) params.set("aseguradora_id", filtroAseg);
      if (filtroEstado && filtroEstado !== "todos") params.set("estado", filtroEstado);
      const qs = params.toString();
      const r = await api.get(`/admin/liquidaciones-aseguradoras${qs ? `?${qs}` : ""}`);
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      console.error(e); toast.error("No se pudo cargar la liquidación");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/aseguradoras").then((r) => setAseguradoras((r.data || []).filter((a) => a.active !== false)));
    load();
    /* eslint-disable-next-line */
  }, []);

  const items = useMemo(() => data?.items || [], [data]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.expediente_id)));
  };

  const sumSeleccion = items.filter((i) => selected.has(i.expediente_id)).reduce((s, i) => s + (i.importe_minuta || 0), 0);

  const accion = async (acc) => {
    if (selected.size === 0) return;
    let numero_factura = null;
    if (acc === "facturar") {
      numero_factura = window.prompt("Nº de factura emitida (opcional):") || "";
    }
    if (!confirm(`¿Aplicar "${acc}" a ${selected.size} expediente(s)?`)) return;
    setBusy(true);
    try {
      await api.post("/admin/liquidaciones-aseguradoras/marcar", {
        expediente_ids: Array.from(selected),
        accion: acc,
        numero_factura,
      });
      await load();
      toast.success(`${selected.size} expediente(s) actualizados`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="liquidaciones-aseguradoras-page">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center gap-3 sticky top-0 z-20">
        <button onClick={() => navigate(-1)} className="text-muted hover:text-foreground inline-flex items-center gap-1 text-xs">
          <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> Atrás
        </button>
        <div className="text-muted">·</div>
        <div className="flex items-center gap-2 min-w-0">
          <Buildings className="w-4 h-4 text-brand" weight="fill" />
          <h1 className="font-display font-bold text-lg tracking-tight truncate">Liquidación con aseguradoras</h1>
        </div>
      </header>

      <div className="p-6 max-w-7xl space-y-4">
        {/* Filtros */}
        <div className="bg-white border border-border rounded-md p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted">Desde (cierre)</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
              data-testid="liqaseg-desde"
              className="block w-full border border-border rounded-md px-3 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted">Hasta</label>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
              data-testid="liqaseg-hasta"
              className="block w-full border border-border rounded-md px-3 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted">Aseguradora</label>
            <select value={filtroAseg} onChange={(e) => setFiltroAseg(e.target.value)}
              data-testid="liqaseg-aseguradora"
              className="block w-56 border border-border rounded-md px-3 py-2 text-sm bg-white">
              <option value="">— Todas —</option>
              {aseguradoras.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted">Estado</label>
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
              data-testid="liqaseg-estado"
              className="block w-44 border border-border rounded-md px-3 py-2 text-sm bg-white">
              <option value="todos">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="facturado">Facturado (sin cobrar)</option>
              <option value="cobrado">Cobrado</option>
            </select>
          </div>
          <button onClick={load}
            data-testid="liqaseg-reload"
            className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5">
            <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> Aplicar
          </button>
        </div>

        {/* KPIs */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Expedientes" value={data.totales.n_expedientes} />
            <Kpi label="Tasación total" value={fmtEUR2(data.totales.tasacion_total)} />
            <Kpi label="Minuta total" value={fmtEUR2(data.totales.minuta_total)} />
            <Kpi label="Pendiente" value={fmtEUR2(data.totales.minuta_pendiente)} accent="slate" />
            <Kpi label="Facturado" value={fmtEUR2(data.totales.minuta_facturada)} accent="amber" />
          </div>
        )}

        {/* Resumen por aseguradora */}
        {data && data.grupos.length > 0 && (
          <Section title="Resumen por aseguradora">
            <div className="border border-[#E8EAED] rounded-[10px] bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="thead-modern">
                  <tr>
                    <th>Aseguradora</th>
                    <th className="!text-right w-16">Nº</th>
                    <th className="!text-right">Tasación</th>
                    <th className="!text-right">Minuta</th>
                    <th className="!text-right">Pendiente</th>
                    <th className="!text-right">Facturada</th>
                    <th className="!text-right">Cobrada</th>
                  </tr>
                </thead>
                <tbody>
                  {data.grupos.map((g, idx) => (
                    <tr key={g.aseguradora_id || g.aseguradora_name} className={`border-t border-[#F1F3F5] hover:bg-[#F5F6F8] transition ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}>
                      <td className="px-4 py-2.5 font-medium">{g.aseguradora_name}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{g.n_expedientes}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtEUR2(g.tasacion_total)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtEUR2(g.minuta_total)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{fmtEUR2(g.minuta_pendiente)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-700">{fmtEUR2(g.minuta_facturada)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{fmtEUR2(g.minuta_cobrada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Acciones de selección */}
        {selected.size > 0 && (
          <div className="sticky top-14 z-10 bg-foreground text-white border border-foreground rounded-md p-3 flex flex-wrap items-center gap-3 shadow-lg">
            <div className="text-sm">
              <strong>{selected.size}</strong> seleccionado(s) · Suma minuta: <strong>{fmtEUR2(sumSeleccion)}</strong>
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={() => accion("facturar")} disabled={busy}
                data-testid="liqaseg-marcar-facturar"
                className="text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-600 rounded-md inline-flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" weight="bold" /> Marcar facturado
              </button>
              <button onClick={() => accion("cobrar")} disabled={busy}
                data-testid="liqaseg-marcar-cobrar"
                className="text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-md inline-flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5" weight="bold" /> Marcar cobrado
              </button>
              <button onClick={() => accion("reabrir-cobro")} disabled={busy}
                data-testid="liqaseg-reabrir-cobro"
                className="text-xs px-3 py-1.5 border border-white/30 hover:bg-white/10 rounded-md">
                Reabrir cobro
              </button>
              <button onClick={() => accion("reabrir-facturacion")} disabled={busy}
                data-testid="liqaseg-reabrir-facturacion"
                className="text-xs px-3 py-1.5 border border-white/30 hover:bg-white/10 rounded-md">
                Reabrir todo
              </button>
            </div>
          </div>
        )}

        {/* Detalle */}
        <Section title="Detalle" desc={loading ? "Cargando…" : `${items.length} expediente(s)`}>
          <div className="border border-[#E8EAED] rounded-[10px] bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="thead-modern">
                  <tr>
                    <th className="!w-8">
                      <input type="checkbox"
                        data-testid="liqaseg-select-all"
                        checked={items.length > 0 && selected.size === items.length}
                        onChange={toggleAll}
                        className="!w-auto" />
                    </th>
                    <th>Cierre</th>
                    <th>Aseguradora</th>
                    <th>Nº Siniestro</th>
                    <th>Asegurado</th>
                    <th>Perito</th>
                    <th className="!text-right">Tasación</th>
                    <th className="!text-right">Minuta</th>
                    <th className="!w-28">Estado</th>
                    <th>Factura</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={11} className="px-4 py-6 text-center text-muted">
                      {loading ? "Cargando…" : "Sin resultados."}
                    </td></tr>
                  ) : items.map((it, idx) => (
                    <tr key={it.expediente_id} className={`border-t border-[#F1F3F5] hover:bg-[#F5F6F8] transition ${selected.has(it.expediente_id) ? "!bg-[#FDF0DB]/60" : (idx % 2 ? "bg-[#FBFBFC]" : "")}`}>
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={selected.has(it.expediente_id)}
                          onChange={() => toggle(it.expediente_id)}
                          data-testid={`liqaseg-row-cb-${it.expediente_id}`}
                          className="!w-auto"
                        />
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(it.fecha_cierre)}</td>
                      <td className="px-3 py-2.5">{it.aseguradora || "—"}</td>
                      <td className="px-3 py-2.5 text-[12.5px] tabular-nums">{it.numero_siniestro || "—"}</td>
                      <td className="px-3 py-2.5 truncate max-w-[180px]">{it.asegurado_nombre || "—"}</td>
                      <td className="px-3 py-2.5 text-[11.5px] tabular-nums">{it.perito_codigo || "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmtEUR2(it.importe_tasacion)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                        {fmtEUR2(it.importe_minuta)}
                        {it.importe_minuta_manual && (
                          <span title="Minuta editada manualmente" className="ml-1 text-[9px] text-amber-700 font-bold inline-flex items-center gap-0.5">
                            <Pencil className="w-2.5 h-2.5" weight="fill" />M
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ESTADO_CLS[it.estado]}`}>{ESTADO_LABEL[it.estado]}</span>
                      </td>
                      <td className="px-3 py-2.5 text-[11.5px] tabular-nums">{it.facturado_numero || "—"}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          onClick={() => navigate(`/review/${it.expediente_id}`)}
                          data-testid={`liqaseg-open-${it.expediente_id}`}
                          className="text-[11px] underline text-muted hover:text-foreground"
                        >Abrir</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const cls = accent === "amber" ? "border-amber-300 bg-amber-50 text-amber-900"
    : accent === "emerald" ? "border-emerald-300 bg-emerald-50 text-emerald-900"
    : accent === "slate" ? "border-slate-300 bg-slate-50 text-slate-900"
    : "border-border bg-white text-foreground";
  return (
    <div className={`rounded-md border ${cls} p-3`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold tracking-tight mt-0.5">{value}</div>
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h2 className="font-semibold text-sm">{title}</h2>
        {desc && <div className="text-[11px] text-muted">{desc}</div>}
      </div>
      {children}
    </div>
  );
}
